import React, { useEffect, useMemo, useState } from 'react';
import { Send, Eye, EyeOff, Info, Mail } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import { usersService } from '../../services/usersService';
import { emailService, isEmailWorkerConfigured } from '../../services/emailService';
import { buildEmailPreviewHtml } from '../../utils/emailPreview';
import { formatFullDate } from '../../utils/format';

/** Fund managers — always shown, default selected (receive a copy of LP sends). */
const MANAGER_COPY_RECIPIENTS = [
  { email: 'gboss4@gmail.com', name: 'Geoffrey' },
  { email: 'griffintboss@gmail.com', name: 'Griffin' },
];

const managerCopyEmails = () =>
  new Set(MANAGER_COPY_RECIPIENTS.map((r) => r.email.toLowerCase()));

const defaultSelectedRecipients = (investorEmails = []) =>
  new Set([...managerCopyEmails(), ...investorEmails]);

function CommunicationsTab({ onStatus, initialDraft, onDraftConsumed }) {
  const [users, setUsers] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selected, setSelected] = useState(() => defaultSelectedRecipients());

  const portalUrl = window.location.origin;
  const workerReady = isEmailWorkerConfigured();

  const investors = useMemo(
    () =>
      users.filter((u) => u.allowed !== false && u.is_fund_admin !== true),
    [users]
  );

  useEffect(() => {
    if (initialDraft) {
      setSubject(initialDraft.subject || '');
      setBody(initialDraft.body || '');
      onDraftConsumed?.();
    }
  }, [initialDraft, onDraftConsumed]);

  useEffect(() => {
    const unsubs = [
      usersService.subscribeToUsers(
        (data) => {
          setUsers(data);
          setLoading(false);
        },
        (err) => {
          onStatus(`Error loading investors: ${err.message}`, true);
          setLoading(false);
        }
      ),
      emailService.subscribeToEmailLog(
        (entries) => setLog(entries),
        (err) => {
          if (err?.code !== 'permission-denied') {
            onStatus(`Error loading email log: ${err.message}`, true);
          }
        }
      ),
    ];
    return () => unsubs.forEach((u) => u());
  }, [onStatus]);

  useEffect(() => {
    if (investors.length > 0 && !initialDraft) {
      setSelected((prev) => {
        const next = new Set(prev);
        managerCopyEmails().forEach((e) => next.add(e));
        investors.forEach((u) => next.add((u.email || u.id).toLowerCase()));
        return next;
      });
    }
  }, [investors, initialDraft]);

  const toggleRecipient = (email) => {
    const key = email.toLowerCase();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(
      defaultSelectedRecipients(investors.map((u) => (u.email || u.id).toLowerCase()))
    );
  };

  const selectNone = () => setSelected(defaultSelectedRecipients());

  const recipientLabel = (email) => {
    const key = email.toLowerCase();
    const manager = MANAGER_COPY_RECIPIENTS.find((r) => r.email === key);
    if (manager) return manager.name;
    const investor = investors.find((u) => (u.email || u.id).toLowerCase() === key);
    return investor?.name || email;
  };

  const previewHtml = useMemo(() => {
    const firstSelected = [...selected][0];
    let previewName = 'Investor';
    if (firstSelected) {
      const key = firstSelected.toLowerCase();
      const manager = MANAGER_COPY_RECIPIENTS.find((r) => r.email === key);
      const investor = investors.find((u) => (u.email || u.id).toLowerCase() === key);
      previewName = manager?.name || investor?.name || firstSelected;
    }
    return buildEmailPreviewHtml({
      subject: subject || 'Subject',
      body: body || 'Your message will appear here.',
      recipientName: previewName,
      portalUrl,
    });
  }, [subject, body, selected, portalUrl, investors]);

  const send = async () => {
    if (!subject.trim()) {
      onStatus('Enter a subject.', true);
      return;
    }
    if (!body.trim()) {
      onStatus('Enter a message.', true);
      return;
    }
    if (selected.size === 0) {
      onStatus('Select at least one recipient.', true);
      return;
    }

    const recipientEmails = [...selected];
    const names = recipientEmails.map(recipientLabel).join(', ');

    if (
      !window.confirm(
        `Send "${subject}" to ${recipientEmails.length} recipient(s)?\n\n${names}`
      )
    ) {
      return;
    }

    try {
      setSending(true);
      onStatus('Sending...');
      const result = await emailService.sendLpEmail({
        subject: subject.trim(),
        body: body.trim(),
        recipientEmails,
        portalUrl,
      });
      if (result.failed > 0) {
        onStatus(`Sent ${result.sent}, failed ${result.failed}. Check email log.`, true);
      } else {
        onStatus(`Email sent to ${result.sent} investor(s).`);
      }
    } catch (err) {
      const msg = err.message || 'Send failed';
      onStatus(
        msg.includes('WORKER') || msg.includes('REACT_APP_EMAIL')
          ? `${msg} — see EMAIL_SETUP.md to deploy the Cloudflare Worker.`
          : msg,
        true
      );
    } finally {
      setSending(false);
    }
  };

  const formatLogDate = (sentAt) => {
    if (!sentAt) return '—';
    try {
      const d = sentAt.toDate ? sentAt.toDate() : new Date(sentAt);
      return formatFullDate(d.toISOString());
    } catch {
      return '—';
    }
  };

  if (loading) return <LoadingSpinner text="Loading communications..." />;

  return (
    <div>
      <div className="a-toolbar">
        <h2 className="a-section-title">Send update</h2>
        <div className="a-toolbar-actions">
          <button
            type="button"
            className="a-btn a-btn-ghost"
            onClick={() => setShowPreview((v) => !v)}
          >
            {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
            {showPreview ? 'Hide preview' : 'Preview'}
          </button>
          <button
            type="button"
            className="a-btn a-btn-primary"
            onClick={send}
            disabled={sending || !workerReady}
          >
            <Send size={16} /> {sending ? 'Sending...' : 'Send email'}
          </button>
        </div>
      </div>

      {!workerReady && (
        <div className="a-setup-banner">
          <strong>Email sending not configured yet.</strong> Upgrade to Workers Paid, onboard your
          domain in Cloudflare Email Sending, deploy the Worker, then set{' '}
          <code>REACT_APP_EMAIL_WORKER_URL</code> in Cloudflare Pages. See <code>EMAIL_SETUP.md</code>{' '}
          in the repo.
        </div>
      )}

      <p className="a-hint" style={{ marginBottom: 16 }}>
        <Info size={14} /> Branded emails are sent via Cloudflare Email Sending ($5/mo Workers Paid).
        Replies go to your Gmail (Reply-To). Recipients must be in the Investors tab with access
        enabled.
      </p>

      <div className={`comm-layout ${showPreview ? 'has-preview' : ''}`}>
        <div className="comm-compose">
          <div className="a-card a-card-pad" style={{ marginBottom: 16 }}>
            <div className="a-field" style={{ marginBottom: 14 }}>
              <label>Subject</label>
              <input
                className="a-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Q1 2026 investor update"
                maxLength={200}
              />
            </div>
            <div className="a-field">
              <label>Message</label>
              <textarea
                className="a-textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={'Dear investor,\n\nA new quarterly update is available on the portal...'}
                rows={10}
                maxLength={12000}
              />
            </div>
          </div>

          <div className="a-card a-card-pad">
            <p className="a-modal-section-label">Fund managers (copy)</p>
            <ul className="comm-recipient-list comm-recipient-list-managers">
              {MANAGER_COPY_RECIPIENTS.map((manager) => {
                const email = manager.email.toLowerCase();
                return (
                  <li key={email}>
                    <label className="comm-recipient-row">
                      <input
                        type="checkbox"
                        checked={selected.has(email)}
                        onChange={() => toggleRecipient(email)}
                      />
                      <span className="comm-recipient-name">{manager.name}</span>
                      <span className="comm-recipient-email">{email}</span>
                    </label>
                  </li>
                );
              })}
            </ul>

            <div className="comm-recipient-head" style={{ marginTop: 18 }}>
              <h3 className="comm-recipient-title">Investors</h3>
              <div className="comm-recipient-actions">
                <button type="button" className="a-btn a-btn-ghost a-btn-sm" onClick={selectAll}>
                  All LPs
                </button>
                <button type="button" className="a-btn a-btn-ghost a-btn-sm" onClick={selectNone}>
                  LPs only off
                </button>
              </div>
            </div>
            {investors.length === 0 ? (
              <p className="a-hint">No investors yet. Add LPs in the Investors tab.</p>
            ) : (
              <ul className="comm-recipient-list">
                {investors.map((user) => {
                  const email = (user.email || user.id).toLowerCase();
                  return (
                    <li key={email}>
                      <label className="comm-recipient-row">
                        <input
                          type="checkbox"
                          checked={selected.has(email)}
                          onChange={() => toggleRecipient(email)}
                        />
                        <span className="comm-recipient-name">{user.name || email}</span>
                        <span className="comm-recipient-email">{email}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {showPreview && (
          <div className="comm-preview">
            <p className="a-modal-section-label">Email preview</p>
            <iframe
              title="Email preview"
              className="comm-preview-frame"
              srcDoc={previewHtml}
              sandbox=""
            />
          </div>
        )}
      </div>

      <div style={{ marginTop: 28 }}>
        <h2 className="a-section-title" style={{ marginBottom: 12 }}>
          <Mail size={18} style={{ verticalAlign: -3, marginRight: 8 }} />
          Send history
        </h2>
        <div className="a-table-wrap">
          <table className="a-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Subject</th>
                <th className="num">Sent</th>
                <th>Recipients</th>
              </tr>
            </thead>
            <tbody>
              {log.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatLogDate(entry.sentAt)}</td>
                  <td>{entry.subject}</td>
                  <td className="num">
                    {entry.sentCount ?? entry.recipients?.length ?? '—'}
                    {entry.failedCount > 0 && (
                      <span className="a-neg" style={{ marginLeft: 6, fontSize: '0.82rem' }}>
                        ({entry.failedCount} failed)
                      </span>
                    )}
                  </td>
                  <td className="comm-log-recipients">
                    {(entry.recipients || []).join(', ')}
                  </td>
                </tr>
              ))}
              {log.length === 0 && (
                <tr>
                  <td colSpan={4} className="a-empty-cell">
                    No emails sent yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CommunicationsTab;
